import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NumberUtils } from '@pk/powerkioskutils';
import { EditorComponent } from '@tinymce/tinymce-angular';

import { RawEditorOptions } from 'tinymce';

import { SecurityService } from 'src/app/security/security.service';
import { environment } from '../../../../../environments/environment';

@Component({
	selector: 'pk-broker-html-editor',
	templateUrl: './html-editor.component.html',
	styleUrls: ['./html-editor.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			multi: true,
			useExisting: HtmlEditorComponent,
		},
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class HtmlEditorComponent implements ControlValueAccessor, AfterViewInit, OnInit {
	@ViewChild('editor') private editor: EditorComponent;

	@Input() invalid = false;
	@Input() placeholderItems: string[] = [];
	@Input() toolbar =
		'undo redo | bold italic underline strikethrough | fontfamily fontsize | alignleft aligncenter alignright | ' +
		'forecolor backcolor | removeformat | bullist numlist | indent outdent | image link unlink charmap';
	@Input() formatItems =
		'bold italic underline strikethrough superscript subscript | fontfamily fontsize align ' + '| forecolor backcolor | language | removeformat';

	@Output() loaded: EventEmitter<boolean> = new EventEmitter();

	public environment = environment;
	public editorConfig: RawEditorOptions;

	private formControlChanged: (text: string) => void;
	private formControlTouched: () => void;
	private value: string;
	private isDisabled: boolean;
	private registeredEditorChange = false;

	constructor(private securityService: SecurityService) {}

	get loggedInUser() {
		return this.securityService.authFields?.loggedInUser;
	}
	get isAdmin() {
		return this.loggedInUser?.isAdmin;
	}

	ngOnInit(): void {
		const toolbar = this.toolbar + (this.placeholderItems?.length ? ' | placeholderItem' : '');

		this.editorConfig = {
			/* eslint-disable @typescript-eslint/naming-convention */
			plugins: 'advlist autolink charmap code emoticons image insertdatetime link lists searchreplace wordcount',
			menubar: 'edit insert format tools',
			menu: {
				edit: { title: 'Edit', items: 'undo redo | cut copy paste pastetext | selectall | searchreplace' },
				insert: { title: 'Insert', items: 'image link | charmap hr | insertdatetime' },
				format: {
					title: 'Format',
					items: this.formatItems,
				},
				tools: { title: 'Tools', items: 'code wordcount' },
			},
			font_family_formats:
				'Andale Mono=andale mono,times; Arial=arial,helvetica,sans-serif; Arial Black=arial black,avant garde; ' +
				'Book Antiqua=book antiqua,palatino; Comic Sans MS=comic sans ms,sans-serif; Courier New=courier new,courier; ' +
				'Georgia=georgia,palatino; Helvetica=helvetica; Impact=impact,chicago; Montserrat=Montserrat,arial; Symbol=symbol; ' +
				'Tahoma=tahoma,arial,helvetica,sans-serif; Terminal=terminal,monaco; Times New Roman=times new roman,times; ' +
				'Trebuchet MS=trebuchet ms,geneva; Verdana=verdana,geneva; Webdings=webdings',
			toolbar,
			toolbar_mode: 'wrap',
			setup: editor => {
				if (this.placeholderItems?.length) {
					editor.ui.registry.addMenuButton('placeholderItem', {
						tooltip: 'Insert a placeholder item that indicates where dynamic data is inserted.',
						text: 'Insert placeholder',
						fetch: success => {
							success(
								this.placeholderItems.map(item => ({
									type: 'menuitem',
									text: item,
									onAction: () => {
										editor.insertContent(`@${item}@`);
										editor.dom.select('');
									},
								})),
							);
						},
					});
				}
			},
			// make all variables non-editable but don't try to replace inside the mailto: part of the anchor
			// tag which then causes display issues in tinymce
			noneditable_regexp: /(?<!mailto:)@\S+@/g,
			noneditable_class: 'placeholder-item',
			content_style: `
				@import url('https://fonts.googleapis.com/css?family=Montserrat&display=swap');
				body {
					font-family: Montserrat;
				}

				span.placeholder-item {
					border: dashed 1px rgb(144, 164, 174);
					padding: 0.25rem;
					position: relative;
					background-color: rgba(207, 216, 220, 0.2);
					cursor: grab !important;
				}`,
			elementpath: false,
			branding: false,
			images_upload_handler: async blobInfo => {
				try {
					if (NumberUtils.bytesToMegabytes(blobInfo.blob().size) > 20) {
						throw new Error('Image must be no more than 20 MB.');
					}

					return `data:image/png;base64,${blobInfo.base64()}`;
				} catch (err) {
					// eslint-disable-next-line no-throw-literal
					throw {
						message: `Failed to add image: ${err?.message}`,
						remove: true,
					};
				}
			},
		};
	}

	public ngAfterViewInit(): void {
		this.editor.writeValue(this.value);
		if (!this.value) {
			this.registerEditorChange();
		}
		this.editor.registerOnTouched(this.formControlTouched);
		this.editor.setDisabledState(this.isDisabled);
		this.editor.onPostRender.subscribe(() => {
			this.loaded.emit(true);
		});
		this.editor.onFocus.subscribe(() => {
			this.registerEditorChange();
		});
	}

	public registerEditorChange(): void {
		if (!this.registeredEditorChange) {
			this.editor.registerOnChange(this.formControlChanged);
			this.registeredEditorChange = true;
		}
	}

	public registerOnChange(fn: (text: string) => void): void {
		this.formControlChanged = fn;
		this.editor?.registerOnChange(fn);
	}

	public registerOnTouched(fn: () => void): void {
		this.formControlTouched = fn;
		this.editor?.registerOnTouched(fn);
	}

	public writeValue(value: string | null): void {
		this.value = value;
		this.editor?.writeValue(value);
	}

	public setDisabledState(isDisabled: boolean): void {
		this.isDisabled = isDisabled;
		this.editor?.setDisabledState(isDisabled);
	}
}
